import {
  Box,
  FieldLabel,
  FieldRichText,
  FieldText,
  FlexBox,
  FormField,
  FormGrid,
  Icon,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { useCallback, useState } from 'react';
import sanitizeHtml from 'sanitize-html';
import { v4 as uuid } from 'uuid';

import { SanitizeHTML } from 'app/utils/htmlSanitizer';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { useIsChanged } from 'components/powerFlowShared/hooks/useIsChanged';
import { KnowledgeBaseLinkPreview } from 'components/settings/components/sections/dixaMessenger/views/flow/components/shared/knowledgeBaseLinkPreview';
import { extractRichTextContentWithHtmlTags } from 'components/settings/components/sections/dixaMessenger/views/flow/components/shared/utils/extractRichTextContentWithHtmlTags';
import { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import { useElevioIntegration } from '../../../../elevioIntegrationProvider';
import { useParsedHtmlTextContent } from '../../../../hooks/useParsedHtmlTextContent';
import { useElevioArticleQuickResponse } from '../../../shared/knowledgeBaseLinkPreview/hooks/useElevioRichTextInputQuickResponse';
import type { InputNode, MessengerInputPayload } from '../../canvas/input';

import { VariableInput } from './components/variableInput/variableInput';
import styles from './inputModal.module.scss';

const messageField = 'message';
const labelField = 'label';
const placeholderField = 'placeholder';

type Props = FlowManagerEditModalComponentT<InputNode>;
type Errors = {
  [Key in
    | typeof messageField
    | typeof labelField
    | typeof placeholderField]?: string;
};

export function InputModal({
  node,
  nodeLabel,
  replaceNode,
  triggerClose,
  scopeVars,
}: Props): JSX.Element {
  const { payload } = node;
  const initMessage = payload?.message ?? '';
  const initLabel = payload?.fieldLabel ?? '';
  const initPlaceholder = payload?.fieldPlaceholder ?? '';

  const [message, setMessage] = useState<string>(initMessage);
  const [label, setLabel] = useState<string>(initLabel);
  const [placeholder, setPlaceholder] = useState<string>(initPlaceholder);
  const [errors, setErrors] = useState<Errors>({});

  const isChanged = useIsChanged(
    [message, initMessage],
    [label, initLabel],
    [placeholder, initPlaceholder],
  );

  const validate = useCallback(() => {
    const errors: Errors = {};
    const messageWithNoTags = sanitizeHtml(message, { allowedTags: [] });

    if (!messageWithNoTags) {
      errors[messageField] = 'Adding a message is required';
    }

    if (!label) {
      errors[labelField] = 'Adding a label is required';
    }

    setErrors(errors);

    return Object.keys(errors).length === 0;
  }, [message, label]);

  const handleSave = useCallback(() => {
    const isValid = validate();

    if (!isValid) {
      return;
    }

    const payload: MessengerInputPayload = {
      ...node.payload,
      message,
      fieldLabel: label,
      fieldPlaceholder: placeholder,
      nextNodeId: node.payload?.nextNodeId || uuid(),
    };

    replaceNode({
      ...(node as InputNode),
      nodeType: FlowNodeTypes.MESSENGER_INPUT,
      icon: 'input',
      text: 'Input',
      isEditable: true,
      isAction: false,
      isRemovable: true,
      payload,
    });
    triggerClose();
  }, [validate, replaceNode, triggerClose, node, message, label, placeholder]);

  const variables = scopeVars.available.filter(
    (label, index, vars) =>
      label !== initLabel && vars.indexOf(label) === index,
  );

  const { elevioIntegration } = useElevioIntegration();
  const hasElevioIntegration = Boolean(elevioIntegration?.companyId);

  const { quickResponseApi, quickResponseRenderer } =
    useElevioArticleQuickResponse({
      search: useParsedHtmlTextContent(message),
    });

  return (
    <Box width="768px">
      <ModalHead onClose={triggerClose} title={nodeLabel} />
      <ModalBody>
        <Text.Paragraph subdued>
          Ask your customers for specific information, e.g. their customer
          number or order number.
        </Text.Paragraph>
        <div className={styles.root}>
          <FormGrid className={styles.column}>
            <FormField>
              <FieldRichText
                label="Your message"
                wrapperClassName={styles.message}
                sanitize={SanitizeHTML.sanitizerForProfile('widgetchat')}
                meta={{ error: errors[messageField] }}
                input={{
                  name: 'message',
                  value: message,
                  onChange: (e) => setMessage(e || ''),
                }}
                quickResponseIcon="elevio"
                quickResponsesApi={
                  hasElevioIntegration ? quickResponseApi : undefined
                }
                renderQuickResponse={
                  hasElevioIntegration ? quickResponseRenderer : undefined
                }
                languages={elevioIntegration?.availableLocales ?? []}
                editorContentExtractor={extractRichTextContentWithHtmlTags}
              />
            </FormField>
            <FormField>
              <FieldText
                label="Placeholder"
                placeholder="E.g 010387"
                meta={{ error: errors[placeholderField] }}
                input={{
                  name: 'placeholder',
                  value: placeholder,
                  onChange: (e) => setPlaceholder(e?.target?.value || ''),
                }}
              />
            </FormField>
            <FormField>
              <VariableInput
                value={label}
                onChange={(variable) => setLabel(variable)}
                variables={variables}
                error={errors[labelField]}
              />
            </FormField>
          </FormGrid>
          <div className={styles.column}>
            <FieldLabel>Preview</FieldLabel>
            <div className={styles.preview}>
              <Box
                padding="250"
                margin="0 64px 0 0"
                borderColor="neutral.200"
                borderWidth="100"
                borderRadius="100"
                dangerouslySetInnerHTML={{ __html: message }}
              />

              <Box margin="8px 0 0 0">
                <KnowledgeBaseLinkPreview text={message} />
              </Box>

              {label && (
                <div className={styles.inputPreview}>
                  <FlexBox
                    borderRadius="100"
                    borderColor="purple.500"
                    borderWidth="100"
                    width="100%"
                    margin="200 0 0"
                    padding="250"
                    gap="200"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <span className={styles.inputPreviewPlaceholder}>
                      {placeholder}
                    </span>
                    <Icon
                      name="paper-plane"
                      color={colors.purple['500']}
                      size={16}
                    />
                  </FlexBox>
                </div>
              )}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFoot
        confirmOnClick={handleSave}
        confirmName={getConfirmButtonVerb(node)}
        confirmProps={{ disabled: !isChanged }}
        cancelOnClick={triggerClose}
      />
    </Box>
  );
}
